import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { createApiClient } from '../lib/api';

const TIERS = ['ALL', 'FREE', 'STARTER', 'PRO', 'EXPERT', 'INTERNAL'] as const;

const tierColors: Record<string, string> = {
  FREE: 'bg-slate-800 text-slate-300',
  STARTER: 'bg-blue-900/50 text-blue-300',
  PRO: 'bg-orange-900/50 text-orange-300',
  EXPERT: 'bg-purple-900/50 text-purple-300',
  INTERNAL: 'bg-green-900/50 text-green-300',
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatShortDate(dateStr: string | null): string {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function UsersPage() {
  const { idToken } = useAuth();
  const api = createApiClient(idToken!);
  const navigate = useNavigate();

  const [tierFilter, setTierFilter] = useState<string>('ALL');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset when filters change
  useEffect(() => {
    setAllUsers([]);
    setNextToken(undefined);
    setHasMore(false);
  }, [tierFilter, debouncedSearch]);

  const tier = tierFilter === 'ALL' ? undefined : tierFilter;

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-users', tierFilter, debouncedSearch],
    queryFn: () => api.listUsers({ search: debouncedSearch || undefined, tier, limit: 50 }),
  });

  // Sync initial data
  useEffect(() => {
    if (data) {
      setAllUsers(data.users || []);
      setNextToken(data.nextToken);
      setHasMore(data.hasMore || false);
    }
  }, [data]);

  // Load more function
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || !nextToken) return;
    setIsLoadingMore(true);
    try {
      const moreData = await api.listUsers({
        search: debouncedSearch || undefined,
        tier,
        limit: 50,
        nextToken,
      });
      setAllUsers((prev) => [...prev, ...(moreData.users || [])]);
      setNextToken(moreData.nextToken);
      setHasMore(moreData.hasMore || false);
    } catch {
      // Silently fail on load more
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, nextToken, debouncedSearch, tier]);

  // Infinite scroll observer
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, loadMore]);

  const tierCounts = data?.tierCounts || {};
  const total = Object.values(tierCounts).reduce((sum: number, v: any) => sum + (v as number), 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Users</h1>
        <div className="flex gap-3 text-sm text-slate-400">
          <span>Total: <span className="text-white font-medium">{total}</span></span>
          {Object.entries(tierCounts).map(([t, count]) => (
            <span key={t}>
              {t}: <span className="text-white font-medium">{count as number}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-orange-500 w-72"
        />
        <div className="flex gap-1">
          {TIERS.map((t) => (
            <button
              key={t}
              onClick={() => setTierFilter(t)}
              className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                tierFilter === t
                  ? 'bg-orange-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-900/30 border border-red-800 rounded-lg p-3 mb-4 text-sm text-red-300">
          {(error as Error).message}
        </div>
      )}

      {/* Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-800">
              <th className="text-left px-4 py-3 text-slate-400 font-medium">Name</th>
              <th className="text-left px-4 py-3 text-slate-400 font-medium">Email</th>
              <th className="text-left px-4 py-3 text-slate-400 font-medium">Tier</th>
              <th className="text-right px-4 py-3 text-slate-400 font-medium">Scans</th>
              <th className="text-right px-4 py-3 text-slate-400 font-medium">Remaining</th>
              <th className="text-left px-4 py-3 text-slate-400 font-medium">Last Login</th>
              <th className="text-left px-4 py-3 text-slate-400 font-medium">Last Scan</th>
              <th className="text-left px-4 py-3 text-slate-400 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={8} className="text-center py-12 text-slate-500">
                  Loading users...
                </td>
              </tr>
            )}
            {!isLoading && allUsers.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-12 text-slate-500">
                  No users found
                </td>
              </tr>
            )}
            {allUsers.map((user: any) => (
              <tr
                key={user.userId}
                onClick={() => navigate(`/users/${user.userId}`)}
                className="border-b border-slate-800/50 hover:bg-slate-800/50 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3 text-white font-medium">
                  {user.name || user.displayName || 'Unnamed'}
                </td>
                <td className="px-4 py-3 text-slate-300">{user.email}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${tierColors[user.subscriptionTier] || tierColors.FREE}`}>
                    {user.subscriptionTier}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-slate-300">{user.totalScans}</td>
                <td className="px-4 py-3 text-right text-slate-300">
                  {user.analysesLimit < 0 ? (
                    <span className="text-green-400">Unlimited</span>
                  ) : (
                    <span>
                      {user.analysesRemaining}
                      <span className="text-slate-500">/{user.analysesLimit}</span>
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-400">{formatShortDate(user.lastLoginAt)}</td>
                <td className="px-4 py-3 text-slate-400">{formatShortDate(user.lastScanAt)}</td>
                <td className="px-4 py-3 text-slate-400">{formatDate(user.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} className="h-4" />
        {isLoadingMore && (
          <div className="text-center py-4 text-slate-500 text-sm">Loading more...</div>
        )}
      </div>
    </div>
  );
}
